import apiClient from '@/shared/api/client';
import type { ApiResponse } from '@/shared/api/types';
import type {
  NotificationCategory,
  NotificationPriority,
  NotificationType,
  PracticeState,
} from './commandCenterApi';

/**
 * Who has to move next for an item to close. This is the primary axis of the
 * Today queue: an item waiting on a practice is not the agency's to work today,
 * and counting it as if it were is what made the old queue unreadable.
 */
export type BallWith = 'us' | 'client' | 'auto';

/** How a practice is doing, in the three categories an agency acts on differently. */
export type PracticeBand = 'risk' | 'setup' | 'steady' | 'snoozed';

export type PillarKey = 'strategy' | 'dataSignal' | 'campaigns' | 'visibility' | 'reputation';

export type Pillars = Record<PillarKey, number | null>;

export interface PortfolioRow {
  id: string;
  name: string;
  vertical: string;
  specialty: string | null;
  status: string;
  state: PracticeState;
  locationsCount: number;
  snoozedAt: string | null;
  band: PracticeBand;

  healthScore: number | null;
  previousHealthScore: number | null;
  healthTrend: 'up' | 'down' | 'stable' | null;
  pillars: Pillars;

  leads30: number;
  leadsDeltaPct: number | null;
  spend30: number;
  cpa: number | null;
  cpaBaseline: number | null;
  /** Weekly conversions, oldest first. */
  trajectory: number[];

  ballWith: BallWith;
  openWork: number;
  openForUs: number;
  lastActivityAt: string | null;
}

export interface PortfolioSummary {
  liveCount: number;
  snoozedCount: number;
  riskCount: number;
  setupCount: number;
  steadyCount: number;
  spend30: number;
  leads30: number;
  blendedCpa: number | null;
}

export interface Mover {
  practiceId: string;
  name: string;
  score: number;
  previousScore: number;
  delta: number;
}

export interface WorkItem {
  id: string;
  practiceId: string;
  practiceName: string;
  locationId: string | null;
  code: string;
  type: NotificationType;
  priority: NotificationPriority;
  category: NotificationCategory;
  title: string;
  description: string;
  actionLabel: string | null;
  deepLink: string | null;
  ballWith: BallWith;
  createdAt: string;
  dueAt: string | null;
  /** Hours until due; negative when overdue. Null when the item has no SLA. */
  hoursUntilDue: number | null;
}

export interface WorkSummary {
  needsUs: number;
  waitingOnClient: number;
  automated: number;
  overdue: number;
  dueToday: number;
  blockedOverThreeDays: number;
  closedThisWeek: number;
  spendAtRisk: number;
  byCategory: Record<NotificationCategory, number>;
}

export interface ClosedItem {
  id: string;
  practiceId: string;
  practiceName: string;
  title: string;
  resolvedAt: string;
}

export type ConnectionState = 'connected' | 'stale' | 'expired' | 'missing';

export interface ConnectionRow {
  provider: string;
  label: string;
  state: ConnectionState;
  lastSyncAt: string | null;
}

export interface TimelineEntry {
  id: string;
  at: string;
  title: string;
  category: string;
  kind: 'opened' | 'closed';
}

export interface SlaStats {
  metOnTimePct: number | null;
  medianResponseHours: number | null;
  breached: number;
}

/** One hired agent's runs in the last 24 hours (backend agency/pulse.service.ts). */
export interface AgentRunCounts {
  roleKey: string;
  personaName: string;
  runs: number;
  failed: number;
}

/** The Overview's Agents chip for one location: share of hired agents' runs that succeeded in 24h. */
export interface LocationAgentHealth {
  locationId: string;
  hired: number;
  score: number | null;
  runs: number;
  failed: number;
  agents: AgentRunCounts[];
  computedAt: string | null;
}

export interface PracticePulse {
  row: PortfolioRow;
  connections: ConnectionRow[];
  locations: { id: string; name: string; city: string | null; status: string }[];
  timeline: TimelineEntry[];
  sla: SlaStats;
  agentHealth: LocationAgentHealth[];
}

export const portfolioApi = {
  getPortfolio: async (params?: {
    search?: string;
    band?: PracticeBand;
    sort?: 'health' | 'spend' | 'name';
  }): Promise<{ rows: PortfolioRow[]; summary: PortfolioSummary }> => {
    const { data } = await apiClient.get<
      ApiResponse<{ rows: PortfolioRow[]; summary: PortfolioSummary }>
    >('/agency/portfolio', { params });
    return data.data;
  },

  getMovers: async (): Promise<Mover[]> => {
    const { data } = await apiClient.get<ApiResponse<Mover[]>>('/agency/portfolio/movers');
    return data.data;
  },

  getWork: async (params?: {
    ball?: BallWith;
    category?: NotificationCategory;
    practiceId?: string;
  }): Promise<WorkItem[]> => {
    const { data } = await apiClient.get<ApiResponse<WorkItem[]>>('/agency/work', { params });
    return data.data;
  },

  getWorkSummary: async (): Promise<{ summary: WorkSummary; closed: ClosedItem[] }> => {
    const { data } =
      await apiClient.get<ApiResponse<{ summary: WorkSummary; closed: ClosedItem[] }>>(
        '/agency/work/summary',
      );
    return data.data;
  },

  getPracticePulse: async (practiceId: string): Promise<PracticePulse> => {
    const { data } = await apiClient.get<ApiResponse<PracticePulse>>(
      `/agency/practices/${practiceId}/pulse`,
    );
    return data.data;
  },
};
